import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FormRow, FormGroup, Label, Input, TicketSectionTitle } from './TicketStyledComponents';

/**
 * Working-order fills panel for listed-derivative block orders (Futures/Options).
 * After the order is released (SENT) the trader records fills as they print; the
 * panel shows Block / Filled / Remaining / VWAP. At EXECUTE the backend posts the
 * filled quantity at the VWAP and cancels any unfilled remainder.
 *
 * Calls /api/trades/{tradeId}/fills (GET/POST/DELETE) and reports the summary up
 * via onSummary so the ticket can gate Execute until at least one fill exists.
 */
export interface FillSummary {
  blockQuantity: number; filledQuantity: number; remainingQuantity: number;
  vwap: number | null; fillCount: number; fullyFilled: boolean;
}

interface Props {
  tradeId: number | null;
  status: string | null;           // transactionStatus
  orderPrice?: string;             // ticket's order/limit price for the "fill remaining" shortcut
  onSummary?: (s: FillSummary) => void;
}

const OrderFills: React.FC<Props> = ({ tradeId, status, orderPrice, onSummary }) => {
  const [fills, setFills] = useState<any[]>([]);
  const [summary, setSummary] = useState<FillSummary | null>(null);
  const [qty, setQty] = useState('');
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const onSummaryRef = useRef(onSummary);
  useEffect(() => { onSummaryRef.current = onSummary; }, [onSummary]);

  const refresh = useCallback(async () => {
    if (!tradeId) return;
    try {
      const res = await axios.get(`/api/trades/${tradeId}/fills`);
      setFills(res.data?.fills || []);
      setSummary(res.data?.summary || null);
      if (res.data?.summary) onSummaryRef.current?.(res.data.summary);
    } catch { /* working order may not exist yet */ }
  }, [tradeId]);

  useEffect(() => { refresh(); }, [refresh]);

  const isWorking = !!tradeId && status === 'SENT';
  const isDone = status === 'EXECUTED' || status === 'SETTLED';

  const addFill = async (q: string, p: string) => {
    if (!tradeId) return;
    setErr('');
    if (!q || Number(q) <= 0 || p === '' || Number(p) < 0) { setErr('Enter a positive quantity and price.'); return; }
    const rem = summary ? summary.remainingQuantity : null;
    if (rem != null && Number(q) > rem) { setErr(`Fill ${q} exceeds remaining ${rem} on the block.`); return; }
    try {
      setBusy(true);
      await axios.post(`/api/trades/${tradeId}/fills`, { fillQuantity: Number(q), fillPrice: Number(p) });
      setQty(''); setPrice('');
      await refresh();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.response?.data?.error || 'Could not add fill.');
    } finally { setBusy(false); }
  };

  const removeFill = async (fillId: number) => {
    if (!tradeId) return;
    try { setBusy(true); await axios.delete(`/api/trades/${tradeId}/fills/${fillId}`); await refresh(); }
    finally { setBusy(false); }
  };

  const fmt = (v: any) => (v == null ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 }));

  // Don't show until the order has been created (needs a trade id).
  if (!tradeId) return null;

  const remaining = summary ? summary.remainingQuantity : 0;

  return (
    <>
      <TicketSectionTitle>Fills {summary?.fullyFilled ? '· Fully Filled' : isWorking ? '· Working' : ''}</TicketSectionTitle>

      {!isWorking && !isDone && (
        <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 10 }}>
          Release the order (Send) to record fills.
        </div>
      )}

      {/* Block / Filled / Remaining / VWAP */}
      <FormRow>
        <Stat label="Block" value={fmt(summary?.blockQuantity)} />
        <Stat label="Filled" value={fmt(summary?.filledQuantity)} accent="#22c55e" />
        <Stat label="Remaining" value={fmt(remaining)} accent={remaining > 0 ? '#f59e0b' : undefined} />
        <Stat label="VWAP" value={summary?.vwap != null ? fmt(summary.vwap) : '—'} />
      </FormRow>

      {/* Recorded fills */}
      {fills.length > 0 && (
        <div style={{ display: 'grid', gap: 6, margin: '8px 0' }}>
          {fills.map(f => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13,
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '8px 12px' }}>
              <span style={{ color: '#94a3b8', width: 28 }}>#{f.seq}</span>
              <span style={{ color: '#fff', fontWeight: 600 }}>{fmt(f.fillQuantity)}</span>
              <span style={{ color: '#94a3b8' }}>@</span>
              <span style={{ color: '#fff', fontWeight: 600 }}>{fmt(f.fillPrice)}</span>
              <span style={{ flex: 1 }} />
              {isWorking && (
                <button type="button" onClick={() => removeFill(f.id)} disabled={busy}
                  style={{ background: 'transparent', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 12 }}>Remove</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add a fill */}
      {isWorking && remaining > 0 && (
        <>
          <FormRow>
            <FormGroup>
              <Label>Fill Quantity</Label>
              <Input type="number" placeholder={`≤ ${fmt(remaining)}`} value={qty} onChange={e => setQty(e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>Fill Price</Label>
              <Input type="number" placeholder="1.00" value={price} onChange={e => setPrice(e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>&nbsp;</Label>
              <button type="button" onClick={() => addFill(qty, price)} disabled={busy}
                style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid rgba(59,130,246,0.5)', background: 'rgba(59,130,246,0.15)', color: '#60a5fa', cursor: 'pointer', fontWeight: 600 }}>
                + Add Fill
              </button>
            </FormGroup>
          </FormRow>
          {orderPrice && Number(orderPrice) >= 0 && (
            <div style={{ marginBottom: 8 }}>
              <button type="button" onClick={() => addFill(String(remaining), orderPrice)} disabled={busy}
                style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(148,163,184,0.4)', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                Fill remaining {fmt(remaining)} @ order price {orderPrice}
              </button>
            </div>
          )}
        </>
      )}
      {err && <div style={{ color: '#f87171', fontSize: 13, marginBottom: 8 }}>{err}</div>}
      {isWorking && summary && summary.filledQuantity === 0 && (
        <div style={{ color: '#f59e0b', fontSize: 13, marginBottom: 8 }}>Record at least one fill before executing.</div>
      )}
    </>
  );
};

const Stat: React.FC<{ label: string; value: string; accent?: string }> = ({ label, value, accent }) => (
  <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '10px 12px' }}>
    <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color: accent || '#fff' }}>{value}</div>
  </div>
);

export default OrderFills;
